import React, { useState, useEffect } from 'react';
import { User, Camera, Globe, Calendar, Check, Loader2 } from 'lucide-react';
import { getCountryFromIP, COUNTRIES } from '../utils/geolocation';

const ProfileSetup = ({ onComplete, userEmail, initialData }) => {
  const data = initialData || {};
  const [username, setUsername] = useState(data.username || '');
  const [age, setAge] = useState(data.age || '');
  const [country, setCountry] = useState(data.country || '');
  const [avatarUrl, setAvatarUrl] = useState(data.avatar_url || '');
  const [isDetectingLocation, setIsDetectingLocation] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(data.avatar_url || '');
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const detectCountry = async () => {
      try {
        const location = await getCountryFromIP();
        if (location.country && location.country !== 'Desconocido') {
          setCountry(location.country);
        }
      } catch (error) {
        console.error('Error detecting country:', error);
      } finally {
        setIsDetectingLocation(false);
      }
    };
    
    detectCountry();
  }, []);

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar tipo de archivo
    if (!file.type.startsWith('image/')) {
      setErrors({ ...errors, avatar: 'Por favor selecciona una imagen válida' });
      return;
    }

    // Validar tamaño (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setErrors({ ...errors, avatar: 'La imagen debe ser menor a 5MB' });
      return;
    }

    // Crear preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result);
    };
    reader.readAsDataURL(file);

    // En producción, aquí subirías a Supabase Storage
    // Por ahora, usamos el preview local
    setAvatarUrl(URL.createObjectURL(file));
  };

  const validate = () => {
    const newErrors = {};

    if (!username.trim()) {
      newErrors.username = 'El nombre de usuario es requerido';
    } else if (username.length < 3) {
      newErrors.username = 'El nombre debe tener al menos 3 caracteres';
    } else if (username.length > 30) {
      newErrors.username = 'El nombre debe tener máximo 30 caracteres';
    }

    if (!age) {
      newErrors.age = 'La edad es requerida';
    } else if (age < 13) {
      newErrors.age = 'Debes tener al menos 13 años';
    } else if (age > 120) {
      newErrors.age = 'Por favor ingresa una edad válida';
    }

    if (!country) {
      newErrors.country = 'Por favor selecciona tu país';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    const profileData = {
      username: username.trim(),
      age: parseInt(age),
      country,
      avatar_url: previewUrl || null,
      profile_completed: true,
    };

    onComplete(profileData);
  };

  return (
    <div className="min-h-screen py-4 px-3 sm:py-6 sm:px-4">
      <div className="max-w-2xl mx-auto bg-white/90 backdrop-blur-sm rounded-3xl shadow-2xl p-4 sm:p-6 md:p-8 border border-purple-100">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8">
          <div className="inline-flex p-3 sm:p-4 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full mb-4">
            <User className="w-8 h-8 sm:w-12 sm:h-12 text-purple-500" />
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2">
            ¡Bienvenido! 🎉
          </h2>
          <p className="text-sm sm:text-base text-gray-600">
            Completa tu perfil para comenzar a jugar
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
          {/* Avatar Upload */}
          <div className="flex flex-col items-center mb-4 sm:mb-6">
            <div className="relative group">
              <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-full overflow-hidden border-4 border-purple-200 shadow-lg bg-gray-100 flex items-center justify-center">
                {previewUrl ? (
                  <img
                    src={previewUrl}
                    alt="Avatar preview"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <User className="w-12 h-12 sm:w-16 sm:h-16 text-gray-400" />
                )}
              </div>
              <label
                htmlFor="avatar-upload"
                className="absolute bottom-0 right-0 p-2 bg-purple-500 hover:bg-purple-600 rounded-full cursor-pointer shadow-lg transition-all transform hover:scale-110"
              >
                <Camera className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </label>
              <input
                id="avatar-upload"
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="hidden"
              />
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Haz clic en la cámara para agregar tu foto
            </p>
            {errors.avatar && (
              <p className="text-red-500 text-sm mt-1">{errors.avatar}</p>
            )}
          </div>

          {/* Username */}
          <div>
            <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
              <User className="w-4 h-4 text-purple-500" />
              Nombre de usuario
            </label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Ej: Dr. García"
              className={`w-full px-3 sm:px-4 py-3 rounded-xl border-2 transition-all focus:outline-none focus:ring-2 focus:ring-purple-400 ${
                errors.username
                  ? 'border-red-300 focus:border-red-400'
                  : 'border-purple-200 focus:border-purple-400'
              }`}
            />
            {errors.username && (
              <p className="text-red-500 text-sm mt-1">{errors.username}</p>
            )}
          </div>

          {/* Age */}
          <div>
            <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
              <Calendar className="w-4 h-4 text-purple-500" />
              Edad
            </label>
            <input
              type="number"
              value={age}
              onChange={(e) => setAge(e.target.value)}
              placeholder="Ej: 25"
              min="13"
              max="120"
              className={`w-full px-3 sm:px-4 py-3 rounded-xl border-2 transition-all focus:outline-none focus:ring-2 focus:ring-purple-400 ${
                errors.age
                  ? 'border-red-300 focus:border-red-400'
                  : 'border-purple-200 focus:border-purple-400'
              }`}
            />
            {errors.age && (
              <p className="text-red-500 text-sm mt-1">{errors.age}</p>
            )}
          </div>

          {/* Country */}
          <div>
            <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
              <Globe className="w-4 h-4 text-purple-500" />
              País
              {isDetectingLocation && (
                <span className="text-xs text-gray-500 flex items-center gap-1">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Detectando...
                </span>
              )}
            </label>
            <select
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              className={`w-full px-3 sm:px-4 py-3 rounded-xl border-2 transition-all focus:outline-none focus:ring-2 focus:ring-purple-400 ${
                errors.country
                  ? 'border-red-300 focus:border-red-400'
                  : 'border-purple-200 focus:border-purple-400'
              }`}
            >
              <option value="">Selecciona tu país</option>
              {COUNTRIES.map((c) => (
                <option key={c.code} value={c.name}>
                  {c.name}
                </option>
              ))}
            </select>
            {errors.country && (
              <p className="text-red-500 text-sm mt-1">{errors.country}</p>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isUploading}
            className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-bold text-base sm:text-lg shadow-lg hover:shadow-xl transition-all transform hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isUploading ? (
              <>
                <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
                Guardando...
              </>
            ) : (
              <>
                <Check className="w-4 h-4 sm:w-5 sm:h-5" />
                Completar Perfil
              </>
            )}
          </button>
        </form>

        {/* Email Info */}
        {userEmail && (
          <div className="mt-4 sm:mt-6 text-center">
            <p className="text-xs sm:text-sm text-gray-500">
              Configurando perfil para: <span className="font-semibold">{userEmail}</span>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfileSetup;
